import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { DashboardPopupService } from './dashboard-popup.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-efile-dashboard-popup',
    templateUrl: './dashboard-popup.component.html',
    styleUrls: ['./dashboard-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DashboardPopupComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private subs = new Subscription();
  private isInitialized = false;

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  filterParams: any = {};

  title: string = '';
  minimise: boolean = false;
  loading: boolean = false;
  submitting: boolean = false;
  currentTheme: string = 'theme-black';
  canDoProdSubmit: boolean = false;
  submissions: any[] = [];
  columns: any[] = [];
  apiUrl: string = '';
  params: any = {};

  showConfirmDialog: boolean = false;
  confirmMessage: string = '';
  confirmAction: string = '';
  confirmIrsId: string = '';

  // ---------------------------------------------------------------------------
  // Table state — sort + search.
  //
  // The legacy template rendered the popup grid with `gt-datatable2`
  // (a DataTables.js wrapper) configured as:
  //   {
  //     searching: true,               // ← toolbar search box
  //     buttons: ['print', 'excel'],   // ← toolbar export buttons
  //     columns: [...]                 // ← all columns sortable by default
  //   }
  // (EfileElementsController.js L1298-1323).
  //
  // The Angular port reproduces these affordances manually:
  //   - `displaySubmissions` is the filtered + sorted projection bound to the
  //     template, kept in sync via `applyTableState`.
  //   - `toggleSort` flips asc/desc on the same column or activates a new
  //     column at asc — same semantics as the DataTables click-to-sort UX.
  //   - `applyFilter` runs a case-insensitive contains match across every
  //     visible column's `data` field (mirroring DataTables global search).
  // ---------------------------------------------------------------------------
  displaySubmissions: any[] = [];
  searchQuery: string = '';
  sortKey: string | null = null;
  sortDir: 'asc' | 'desc' = 'asc';

  // ---------------------------------------------------------------------------
  // Direct-input mode (in-app modal usage). Mirrors the override-modal pattern:
  // when the host renders `<gtw-efile-dashboard-popup>` inline (rather than via
  // the AngularJS `<gtw-efile-dashboard-popup-ce>` custom element), it can
  // pass the popup row payload directly. These inputs take precedence over
  // anything we'd derive from `workspaceFactory.activeScreen`.
  // ---------------------------------------------------------------------------
  private hasInputRowData = false;

  @Input('popup-row-data')
  set _popupRowData(value: any) {
    if (value && typeof value === 'object' && Object.keys(value).length > 0) {
      this.rowData = value;
      this.hasInputRowData = true;
      // If inputs arrive after ngOnInit fired, run init now.
      if (!this.isInitialized) {
        this.tryInit();
      }
    }
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private dashboardPopupService: DashboardPopupService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext()));
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext()));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    // When the host already supplied a popup payload via `[popup-row-data]`,
    // skip the workspace-derived rebuild — re-running it would clobber the
    // host-provided rowData with whatever happens to be on the active screen.
    if (this.hasInputRowData) {
      const gc = generalConfigFromGlobal(this.gtwGlobal);
      this.baseURLs = {
        api: gc.api,
        admin: gc.admin,
        custom: gc.custom,
        efile: gc.efile,
        pdfEngine: gc.pdfEngine,
        bulkPdfEngine: gc.bulkPdfEngine,
      };
      const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
      this.USER_SETTINGS = shell.userSettingsData;
      this.SERVER_CONFIG = shell.serverConfigData;
      this.GENERAL_CONFIG = shell.generalConfigData;
      this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
      this.tryInit();
      this.cdr.markForCheck();
      return;
    }

    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.USER_SETTINGS = shell.userSettingsData;
    this.SERVER_CONFIG = shell.serverConfigData;
    this.GENERAL_CONFIG = shell.generalConfigData;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.SERVER_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    // In direct-input mode, `popup-row-data` can arrive before workspace
    // context hydration. Guard init until base_url is available, otherwise
    // the first load call is built as `undefined/loadJsonObject?...`.
    if (!this.GENERAL_CONFIG?.base_url) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.title = this.rowData.title || '';
    this.apiUrl = this.rowData.api_url || '';
    this.params = this.rowData.params || {};
    this.columns = this.rowData.columns || [];
    this.currentTheme = this.USER_SETTINGS?.user?.selected_theme || 'theme-black';
    this.canDoProdSubmit = this.SERVER_CONFIG?.configSettings?.efile_consol_prod_submission_users
      ? this.SERVER_CONFIG.configSettings.efile_consol_prod_submission_users.includes(this.USER_SETTINGS?.user?.sso_id)
      : false;

    this.loadSubmissions();
  }

  private loadSubmissions(): void {
    this.loading = true;
    this.cdr.detectChanges();

    this.dashboardPopupService.loadSubmissions(
      this.GENERAL_CONFIG.base_url,
      this.apiUrl,
      this.params
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.loading = false;
        if (response.jsonObject) {
          this.submissions = response.jsonObject;
        }
        this.applyTableState();
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.loading = false;
        console.error('Error loading submissions:', error);
        this.cdr.detectChanges();
      }
    );
  }

  // ---------------------------------------------------------------------------
  // Sort + search (DataTables parity)
  // ---------------------------------------------------------------------------

  /**
   * Header click toggles asc → desc on the active column or activates a new
   * column at asc. Action / null-data columns are skipped to match the legacy
   * DataTables behaviour (`columns: [{data: null, ...}]` was rendered as a
   * non-sortable computed column).
   */
  toggleSort(col: any): void {
    if (!col || col.data == null) return;
    const key = col.data as string;
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }
    this.applyTableState();
    this.cdr.markForCheck();
  }

  sortIconClass(col: any): string {
    if (!col || col.data == null) return '';
    if (this.sortKey !== col.data) return 'fa fa-sort';
    return this.sortDir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc';
  }

  isSortable(col: any): boolean {
    return !!col && col.data != null;
  }

  /**
   * Per-cell text projection used by the template's non-link branch.
   * Legacy `efileFactory.submissionListColumns` (EfileService.js L96)
   * formatted the Submitted Date column with `$filter('date')(data, 'medium')`,
   * which produces strings like "May 18, 2026 8:04:21 PM". Other columns
   * pass through unchanged.
   */
  formatCell(row: any, col: any): string {
    const value = row?.[col?.data];
    if (value == null) return '';
    if (col?.isDate) {
      return this.formatMediumDate(value);
    }
    return String(value);
  }

  /** Matches AngularJS `$filter('date')(value, 'medium')` output. */
  private formatMediumDate(value: any): string {
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value ?? '');
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ];
    const month = months[d.getMonth()];
    const day = d.getDate();
    const year = d.getFullYear();
    const h24 = d.getHours();
    const ampm = h24 >= 12 ? 'PM' : 'AM';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    const mi = String(d.getMinutes()).padStart(2, '0');
    const se = String(d.getSeconds()).padStart(2, '0');
    return `${month} ${day}, ${year} ${h12}:${mi}:${se} ${ampm}`;
  }

  onSearchChange(value: string): void {
    this.searchQuery = value || '';
    this.applyTableState();
    this.cdr.markForCheck();
  }

  /**
   * Recomputes `displaySubmissions` from the source `submissions` list by
   * applying the active search filter and then the active sort. Called on
   * every load / sort / filter change so the template binds against a stable
   * projection under OnPush.
   */
  private applyTableState(): void {
    const visibleCols = (this.columns || []).filter((c: any) => c?.visible !== false);
    const query = (this.searchQuery || '').trim().toLowerCase();

    let rows = Array.isArray(this.submissions) ? [...this.submissions] : [];

    if (query) {
      rows = rows.filter((row: any) =>
        visibleCols.some((c: any) => {
          if (!c || c.data == null) return false;
          const value = row?.[c.data];
          return value != null && String(value).toLowerCase().includes(query);
        }),
      );
    }

    if (this.sortKey) {
      const key = this.sortKey;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      rows.sort((a: any, b: any) => {
        const av = a?.[key];
        const bv = b?.[key];
        if (av == null && bv == null) return 0;
        if (av == null) return -1 * dir;
        if (bv == null) return 1 * dir;
        // Numeric comparison when both sides parse as finite numbers.
        const an = Number(av);
        const bn = Number(bv);
        if (!isNaN(an) && !isNaN(bn) && isFinite(an) && isFinite(bn)
            && String(an) === String(av) && String(bn) === String(bv)) {
          return (an - bn) * dir;
        }
        // Date comparison when both sides parse as valid dates.
        const ad = Date.parse(String(av));
        const bd = Date.parse(String(bv));
        if (!isNaN(ad) && !isNaN(bd)) {
          return (ad - bd) * dir;
        }
        return String(av).localeCompare(String(bv)) * dir;
      });
    }

    this.displaySubmissions = rows;
  }

  sendToIRS(env: string, irsId: string): void {
    this.requestGridUpdate.emit(JSON.stringify({ action: 'submit-valid-return', env, irsId }));
  }

  markAsReviewed(irsId: string): void {
    this.confirmMessage = 'Are you sure you want to mark this return as reviewed?';
    this.confirmAction = 'markAsReviewed';
    this.confirmIrsId = irsId;
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  discardReturn(irsId: string): void {
    this.confirmMessage = 'Are you sure you want to discard this return?';
    this.confirmAction = 'discardReturn';
    this.confirmIrsId = irsId;
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirm(): void {
    this.showConfirmDialog = false;

    if (this.confirmAction === 'markAsReviewed') {
      this.dashboardPopupService.markAsReviewed(this.GENERAL_CONFIG.base_url, this.confirmIrsId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (response: any) => {
          if (response.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Successfully marked as reviewed!', 'gtw-snackbar--green');
            this.loadSubmissions();
          } else {
            this.gtwSnackbarService.openSnackBar('Failed to mark as reviewed!', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        (error: any) => {
          this.gtwSnackbarService.openSnackBar('Error marking as reviewed.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
    } else if (this.confirmAction === 'discardReturn') {
      this.dashboardPopupService.discardReturn(
        this.baseURLs.efile,
        this.params.submission_id,
        this.confirmIrsId
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (response: any) => {
          if (response.callSuccess === '1' || response.data?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Successfully discarded the return', 'gtw-snackbar--green');
            this.loadSubmissions();
          } else {
            this.gtwSnackbarService.openSnackBar('Something went wrong!', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        (error: any) => {
          this.gtwSnackbarService.openSnackBar('Something went wrong!', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
    }
  }

  onCancelConfirm(): void {
    this.showConfirmDialog = false;
    this.cdr.detectChanges();
  }

  downloadFile(type: string, irsId: string): void {
    this.dashboardPopupService.downloadFile(this.baseURLs.efile, irsId, type).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: ArrayBuffer) => {
        if (data && data.byteLength > 0) {
          let fileType = type + '_';
          let ext: string;
          switch (type) {
            case 'manifest':
            case 'receipt':
              ext = 'xml';
              break;
            case 'ack':
              ext = 'pdf';
              break;
            case 'ifa':
              ext = 'tf';
              break;
            default:
              fileType = '';
              ext = 'zip';
          }
          const blob = new Blob([data], { type: 'application/' + ext });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${fileType}${irsId}.${ext}`;
          link.click();
          link.remove();
          URL.revokeObjectURL(url);
        } else {
          this.gtwSnackbarService.openSnackBar('No file available to download', 'gtw-snackbar--yellow');
        }
      },
      (error: any) => {
        this.gtwSnackbarService.openSnackBar('Error downloading file.', 'gtw-snackbar--red');
      }
    );
  }

  printTable(): void {
    const visibleCols = this.columns.filter((col: any) => col.visible !== false);
    // Mirror DataTables Buttons: exports respect the active search/sort
    // (`buttons.exportData({ search: 'applied' })`).
    const rowsToPrint = this.displaySubmissions || [];
    if (!visibleCols.length || !rowsToPrint.length) return;

    const headerHtml = visibleCols.map((c: any) => `<th>${this.escapeHtml(c.title || '')}</th>`).join('');
    const bodyHtml = rowsToPrint
      .map((row: any) => {
        const tds = visibleCols
          .map((c: any) => `<td>${this.escapeHtml(row?.[c.data] ?? '')}</td>`)
          .join('');
        return `<tr>${tds}</tr>`;
      })
      .join('');

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      this.gtwSnackbarService.openSnackBar('Popup blocked — unable to open print view.', 'gtw-snackbar--yellow');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>${this.escapeHtml(this.title || 'Dashboard Popup')}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 12px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 6px 10px; text-align: left; }
            th { background: #e0e0e0; color: #333; }
          </style>
        </head>
        <body>
          <table>
            <thead><tr>${headerHtml}</tr></thead>
            <tbody>${bodyHtml}</tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }

  exportToExcel(): void {
    const visibleCols = this.columns.filter((col: any) => col.visible !== false);
    // Same export-respects-search behaviour as the legacy DataTables build.
    const rowsToExport = this.displaySubmissions || [];
    if (!visibleCols.length || !rowsToExport.length) return;

    const header = visibleCols.map((c: any) => this.csvCell(c.title || '')).join(',');
    const rows = rowsToExport.map((row: any) =>
      visibleCols.map((c: any) => this.csvCell(row?.[c.data] ?? '')).join(',')
    );
    const csv = [header, ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(this.title || 'dashboard-popup').replace(/\s+/g, '_')}.csv`;
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  private csvCell(value: any): string {
    const text = String(value ?? '');
    return `"${text.replace(/"/g, '""')}"`;
  }

  private escapeHtml(value: any): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
